import { gtpToPoint } from "../go/coordinates";
import { adjacentGroups, applyMove, colorToStone, opponent, opponentStone } from "../go/rules";
import type { Board, Color, Point } from "../go/types";
import type { AnalysisInput, AnalysisReason, MoveAnalysis } from "./types";

// One ply of the engine's principal variation, replayed on the real rules engine so the coaching
// reflects what concretely happens in the line (captures, ataris) rather than echoing the raw
// move list.
export interface PvPly {
  ply: number; // 1-based depth along the PV
  color: Color; // who played this move
  label: string;
  point: Point;
  captures: number; // stones this move captured
  ataris: number; // opponent stones this move left in atari
}

export interface PvReplay {
  plies: PvPly[];
  finalBoard: Board; // position after the last replayed ply
}

function sideName(color: Color): string {
  return color === "black" ? "Black" : "White";
}

// Replay a PV from a position, stopping at the first illegal/unparseable move (which can happen at
// very low visit counts where the PV tip is policy-greedy and not fully read out).
export function replayPvLine(board: Board, size: number, toPlay: Color, pv: string[]): PvReplay {
  let workingBoard = board;
  let color = toPlay;
  const plies: PvPly[] = [];

  for (const raw of pv) {
    const label = raw.trim().toUpperCase();
    if (!label) continue;
    if (label === "PASS") {
      // A pass in the line ends the tactical flow we can reason about; stop the trace here.
      break;
    }
    const point = gtpToPoint(label, size);
    if (!point) break;
    const result = applyMove(workingBoard, size, point, color);
    if (!result.ok) break;

    const enemyStone = opponentStone(colorToStone(color));
    const enemyGroups = adjacentGroups(result.board, size, point, enemyStone);
    const ataris = enemyGroups
      .filter((group) => group.liberties.size === 1)
      .reduce((sum, group) => sum + group.stones.length, 0);

    plies.push({ ply: plies.length + 1, color, label, point, captures: result.captures, ataris });
    workingBoard = result.board;
    color = opponent(color);
  }

  return { plies, finalBoard: workingBoard };
}

// Build coaching reasons by reading the engine's main line forward and describing what happens.
// Produces a "Main line" variation reason plus follow-up tactical/risk notes for events past the
// first move (the first move's own tactics are covered by the verified single-move checks).
export function tracePvReasons(input: AnalysisInput, move: MoveAnalysis): AnalysisReason[] {
  const pv = (move.pv ?? []).filter(Boolean);
  if (pv.length === 0) {
    return [];
  }

  const { plies } = replayPvLine(input.board, input.size, input.toPlay, pv);
  if (plies.length === 0) {
    return [
      {
        category: "variation",
        label: "Main line",
        detail: `The engine starts with ${move.label}, but the line could not be read out further from this position.`,
        impact: 60
      }
    ];
  }

  const mover = input.toPlay;
  const depth = plies.length;
  const seq = plies.map((ply) => ply.label).join(" ");

  let moverCaptures = 0;
  let oppCaptures = 0;
  let firstMoverCapture: PvPly | undefined;
  let firstForcing: PvPly | undefined;
  for (const ply of plies) {
    if (ply.captures > 0) {
      if (ply.color === mover) {
        moverCaptures += ply.captures;
        firstMoverCapture ??= ply;
      } else {
        oppCaptures += ply.captures;
      }
    }
    if (ply.ataris > 0 && !firstForcing) {
      firstForcing = ply;
    }
  }

  const reasons: AnalysisReason[] = [];

  let detail = `Reading ${depth} move${depth === 1 ? "" : "s"} ahead (${seq})`;
  if (depth === 1) {
    detail += " — only one move deep here, so treat it as a shallow read and confirm the follow-up yourself.";
  } else if (moverCaptures > 0 && firstMoverCapture) {
    detail += `, ${sideName(mover)} captures ${moverCaptures} stone${moverCaptures === 1 ? "" : "s"} (starting at ${firstMoverCapture.label} on move ${firstMoverCapture.ply}).`;
  } else if (firstForcing) {
    detail += `, ${sideName(firstForcing.color)} stays forcing with atari at ${firstForcing.label} on move ${firstForcing.ply}.`;
  } else {
    detail += " — a quiet, positional line with no captures; compare the frameworks each side builds.";
  }
  reasons.push({ category: "variation", label: "Main line", detail, impact: 76 });

  const laterCapture = plies.find((ply) => ply.ply >= 2 && ply.captures > 0);
  if (laterCapture) {
    reasons.push({
      category: "tactical",
      label: "Captures in the follow-up",
      detail: `Move ${laterCapture.ply} (${laterCapture.label}) captures ${laterCapture.captures} stone${laterCapture.captures === 1 ? "" : "s"}, so the sequence is hard for the opponent to refuse.`,
      impact: 64
    });
  } else {
    const laterForcing = plies.find((ply) => ply.ply >= 2 && ply.ataris > 0);
    if (laterForcing) {
      reasons.push({
        category: "tactical",
        label: "Forcing follow-up",
        detail: `Move ${laterForcing.ply} (${laterForcing.label}) leaves stones in atari, keeping the initiative through the line.`,
        impact: 58
      });
    }
  }

  if (oppCaptures > 0) {
    reasons.push({
      category: "risk",
      label: "Trade in this line",
      detail: `The line also gives ${sideName(opponent(mover))} ${oppCaptures} stone${oppCaptures === 1 ? "" : "s"} — read whether the exchange is worth it before committing.`,
      impact: -28
    });
  }

  return reasons;
}
